import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import BonusOffer from '../../BonusOffer/BonusOffer';
import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFGender from '../components/RMFGender/RMFGender';
import RMFIdentityVerification from '../components/RMFIdentityVerification/RMFIdentityVerification';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPhone from '../components/RMFPhone/RMFPhone';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfESFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const forkRmfBonusOffer = new Fork(
    Guard.ShowWelcomeBonusES,
    [BonusOffer, RMFLegalName],
    [RMFLegalName]
  );

  const invokeRmfDobUpdate = new Invoke(Service.RmfDobUpdate, forkRmfBonusOffer, RMFDOB);

  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate],
    [forkRmfBonusOffer]
  );

  const invokeSignupDob = new Invoke(Service.SignupDobFetch, forkDobCheck);

  const invokeCrmBonus = new Invoke(Service.SetCRMBonus, invokeSignupDob);
  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const forkVerification = new Fork(Guard.UserStatusInSpain, [forkRmfExit], [RMFVerification]);

  const forkDepositProcess = new Fork(
    Guard.doesJumpFromDepositProcess,
    [forkRmfExit],
    [forkVerification]
  );

  const invokeEnableRealMoneyFlag = new Invoke(
    Service.RmfEnableRealMoneyFlagSpain,
    forkDepositProcess,
    RMFIdentityVerification
  );

  const invokeLegalNameSave = new Invoke(Service.RmfLegalNameSave, RMFPhone, RMFLegalName);
  const invokeGenderSave = new Invoke(Service.RmfGenderSave, RMFAddress, RMFGender);
  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFIdentityVerification, RMFAddress);
  const invokeMobilePhoneSave = new Invoke(Service.RmfMobilePhoneSave, RMFGender, RMFPhone);

  const invokeIdentityVerificationSave = new Invoke(
    Service.RmfIdentifyVerificationSave,
    invokeEnableRealMoneyFlag,
    RMFIdentityVerification
  );

  return [
    invokeCrmBonus,
    invokeSignupDob,
    forkDobCheck,
    forkRmfBonusOffer,
    invokeLegalNameSave,
    RMFPhone,
    invokeMobilePhoneSave,
    RMFGender,
    invokeGenderSave,
    RMFAddress,
    invokeAddressSave,
    RMFIdentityVerification,
    invokeIdentityVerificationSave,
    invokeEnableRealMoneyFlag,
    forkDepositProcess,
    jumpToFeatureStep
  ];
};

export default useRmfESFlow;
